import { environment } from '../../../environments/environment';
import { SubmissionDefaults } from '../../services/submission.service';

export interface AddressDetails {
  addressId: Number;
  country: String;
  propertyName: String;
  unitNumber: String;
  streetNumber: String;
  streetName: String;
  streetType: String;
  suburb: String;
  postcode: String;
  state: String;
  poBox: String;
  addressText: String;
  rpId: String;
  occupancy?: {
    unit: String;
    value: Number;
  };
  residencyType?: String;
}

export interface FrequencyUnitDetails {
  frequency: String;
  amount: Number;
  employmentId?: Number;
  ownership?: Number;
  link?: String;
}

export interface Party {
  id: any;
  partyRoleId?: any;
  type: String;
  referenceType?: String;
  value?: Number;
  roles?: String[];
  trust?: Boolean;
  trustPurposeType?: String;
  trustees?: String[];
  directors?: String[];
  name?: String; // company name
  tradingName?: String;
  abn?: String;
  acn?: String;
  externalRef?: String;
  salutation?: String;
  firstName?: String;
  otherNames?: String[];
  lastName?: String;
  dateOfBirth?: Date;
  driversLicence?: String;
  driversLicenceState?: String;
  driversLicenceCardNumber?: String;
  contact?: any;
  dependents?: Number;
  dependentsYoung?: Number;
  maritalStatus?: String;
  electronicIdentity?: Boolean;
  electronicDelivery?: Boolean;
  privacyConsent?: Boolean;
  auCitizen?: Boolean;
  isNccpQuestionCovered?: Boolean;
  nccpNotes?: String;
  creditReportEnquiryId?: String;
  address?: {
    residential?: AddressDetails,
    postal?: AddressDetails,
    previous?: AddressDetails,
    postSettlement?: AddressDetails,
    trading?: AddressDetails
  };
  income?: {
    salary?: any, // can be an array of frequency unit details or single
    rental?: FrequencyUnitDetails[],
    familyAllowance?: FrequencyUnitDetails,
    maintenance?: FrequencyUnitDetails,
    other?: FrequencyUnitDetails,
    employments?: [
      {
        id?: String,
        employer?: String,
        occupation?: String,
        basis?: String,
        duration?: {
          unit?: String,
          value?: Number
        }
      }
    ]
  };
}

export interface Collateral {
  id: any;
  type: String;
  // auto
  description?: String;
  make?: String;
  model?: String;
  year?: Number;
  usage?: String;
  vin?: String;
  nvic?: String;
  className?: String;
  gvm?: String;
  co2?: String;
  category?: String;
  odometerReading?: Number;
  variant?: String;
  // home
  address?: AddressDetails;
  dates?: Object; // TODO:
  occupancyType?: String;
  propertyType?: String;
  mortgageType?: String;
  propertyStatusType?: String;
  valuation?: Object; // TODO:
  estimatedValue?: Number;
  parties?: Object;
  class?: String;
}

export interface PurposeMember {
  id: String;
  guarantor?: Boolean;
  role: String;
}
export interface Purpose {
  id: any;
  isStaffApplication: Boolean;
  parentApplication: String;
  type: String;
  channel: String;
  externalRef: String;
  members: PurposeMember[];
  collaterals: String[]; // list of collaterals included in the loan
  gstFinanced: Boolean;
  homeOwnerDiscount: Boolean;
  product?: Product;
  purpose: String;
  paidAtSettlement: Number;
  loanAmount: Number;
  depositAmount: Number;
  purchasePrice: Number;
  balloon: Number;
  separateContracts: Boolean;
  term?: Term;
  interestOnlyTerm?: Term;
  disbursements: Transaction[];
  fees: Transaction[];
  deposits?: Transaction[];
  products?: ProductTerm[];
  sacrifice?: String;
  applicationType?: String;
  furtherAdvanceApplicationType?: String;
  securityValue?: Number;
  securityAddress?: AddressDetails;
  securityDescription?: String;
  securityDetails?: String;
  notes?: String;
  deliveryOptions?: DeliveryOptions;
  skipAutomaticDisbursementCreation?: Boolean;
  files: String[]; // list of files to be added to this product application
  propertyFound: Boolean;
  mortgageInsurance: Object;
}
export interface S3File {
  key: String;
  name: String;
  documentTypeCode: String;
  id: any;
  applicant?: String;
  collateral?: String;
}

export interface DeliveryOptions {
  method: String;
  toBroker: Boolean;
}
export interface Transaction {
  type: String;
  description: String;
  details?: String;
  lender?: String;
  securityAddress?: AddressDetails;
  amount: Number;
}

export interface Product {
  name: String;
  pricing?: String;
  monthlyFee?: Number;
  isShariaCompliant?: String;
  isSMSF?: Boolean;
  isGreen?: Boolean;
  isReferenceRateDisclosed?: Boolean;
  referenceRate?: String;
  honeymoon?: Term;
  allowCards?: String;
  rtb: Number;
  rate: Number;
  fixedTerm: Term;
  margin: Number;
  interestType: String;
  documentationType: String;
  adjustment?: Number;
  adjustmentType?: String;
  adjustmentExpiry?: String; // TODO not sure if DATE or STRING or NUMBER
  displayName: String;
}

export interface Term {
  unit: String;
  frequency?: String;
  value: Number;
}

export interface ProductTerm {
  product: Product;
  amount: Number;
  term: Term;
  interestOnlyTerm: Term;
  fixedRateTerm: Term;
  repayment: String;
  parentSettlementXref?: String;
}

export interface Expense {
  amount: Number;
  frequency: String;
  refinancing?: Boolean;
  interestOnly?: Boolean;
}

export interface Lead {
  channel: String;
  relatesTo: String;
  shop: String;
  creationSource: String;
}

export interface Relationship {
  from: String; // applicant 1
  to?: String; // applicant 2
  type: String;
}
export interface Servicing {
  expenses: any[];
  assets: any[];
  liabilities: any[];
  relationships: Relationship[];
}

export interface BaseModel {
  parties: Party[];
  collaterals: Collateral[];
  files: S3File[];
  purposes: Purpose[];
  servicing: Servicing[];
  lead: Lead;
  wizardModel: any;
}

/**
 * Make sure this is reflective of any changes FROM the submission-api
 * You can set default values for the submission model here. Of course it can be overridden
 */
export class BaseSubmissionModel implements BaseModel {
  parties: Party[] = Array<Party>();
  collaterals: Collateral[] = Array<Collateral>();
  files: S3File[] = Array<S3File>();
  purposes: Purpose[] = Array<Purpose>();
  servicing: Servicing[] = Array<Servicing>();
  lead: any = {
    channel: '',
    creationSource: '',
    shop: '',
    relatesTo: '',
    postcode: null,
    status: null,
    source: null,
    productCategory: null
  };
  wizardModel: any;

  constructor(private defaults: SubmissionDefaults = null) {
    console.log('BaseSubmissionModel defaults', defaults);

    if (defaults && defaults.salesRelatesTo) {
      this.lead.relatesTo = defaults.salesRelatesTo;
    }
    if (defaults && defaults.shop) {
      this.lead.shop = defaults.shop;
    }

    if (defaults && defaults.channel) {
      this.lead.channel = defaults.channel;
    }

    console.log('BaseSubmissionModel lead', this.lead);
  }
}
